import { reportToSentry } from '@sonnen/web-utils';
import { ReactNode, useEffect } from 'react';
import { useAppContext } from '../../AppContext/AppContext';
import { useTranslation } from '../../i18n/i18n';
import { useOfferContext } from '../../OfferContext/OfferContext';
import OfferCalculationForm from '../../Pages/TariffOverview/OfferCalculationForm/OfferCalculationForm';
import { PageBackground } from '../../types';
import { getContractCreationBlocked } from '../../utils/utils';
import ContractCreationBlocked from '../ContractCreationBlocked/ContractCreationBlocked';
import { FullContentMessage } from '../FullContentMessage/FullContentMessage';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { OrderFlowPage, OrderFlowPageProps } from '../OrderFlowPage/OrderFlowPage';
import styles from './OfferCalculationPage.module.css';

/**
 * Renders children inside an OrderFlowPage component, underneath the offer calculation form.
 * While loading, it displays a spinner instead of the children.
 * If no offer is available and contract creation is not blocked, it shows a message indicating missing
 * data.
 */
export const OfferCalculationPage = ({
  children,
  title: defaultTitle,
  dataTestId,
  sideCard,
}: OrderFlowPageProps) => {
  const { selectedSite, orderFlow, setCurrentBackground, user } = useAppContext();
  const { offer, offerLoading } = useOfferContext();
  const { t } = useTranslation();

  const contractCreationBlocked = getContractCreationBlocked(selectedSite, offer, orderFlow);

  // Log loading state
  useEffect(() => {
    if (offerLoading) {
      const userId = user?.contact?.email || user?.id || 'unknown';
      reportToSentry('Offer calculation in progress', {
        tag: 'offer_calculation',
        level: 'info',
        context: {
          component: 'OfferCalculationPage',
          event: 'loading',
          userId,
          orderFlow,
          siteId: selectedSite?.id,
        },
      });
    }
  }, [offerLoading, user, selectedSite?.id, orderFlow]);

  // Log when offer data arrives
  useEffect(() => {
    if (offer && !offerLoading) {
      const userId = user?.contact?.email || user?.id || 'unknown';
      reportToSentry('Offer data received in OfferCalculationPage', {
        tag: 'offer_calculation',
        level: 'info',
        context: {
          component: 'OfferCalculationPage',
          event: 'offer_received',
          userId,
          orderFlow,
          siteId: selectedSite?.id,
          hasEnergyDynamic: !!offer?.energyDynamic,
          hasBatteryOptimization: !!offer?.batteryOptimization,
        },
      });
    }
  }, [offer, offerLoading, user, selectedSite?.id, orderFlow]);

  // Report to Sentry only when eligibility is blocked AFTER loading is complete
  if (contractCreationBlocked && !offerLoading) {
    const userId = user?.contact?.email || user?.id || 'unknown';
    reportToSentry('Battery Optimization eligibility blocked', {
      tag: 'offer_calculation',
      level: 'warning',
      context: {
        component: 'OfferCalculationPage',
        event: 'eligibility_blocked',
        userId,
        blockedReason: contractCreationBlocked,
        orderFlow,
        siteId: selectedSite?.id,
        hasOffer: !!offer,
      },
    });
  }

  // Log when no offer is available
  if (!offerLoading && !offer && !contractCreationBlocked) {
    const userId = user?.contact?.email || user?.id || 'unknown';
    reportToSentry('No offer available - missing data', {
      tag: 'offer_calculation',
      level: 'warning',
      context: {
        component: 'OfferCalculationPage',
        event: 'missing_data',
        userId,
        orderFlow,
        siteId: selectedSite?.id,
      },
    });
  }

  let content: ReactNode = children;
  let title: ReactNode = defaultTitle;
  if (offerLoading) {
    setCurrentBackground();
    content = <LoadingSpinner text={t('offer.loading')} />;
  } else if (contractCreationBlocked) {
    title = t('contractCreationBlocked.backToEnergyStore');
    content = <ContractCreationBlocked reason={contractCreationBlocked} />;

    setCurrentBackground(
      contractCreationBlocked === 'sonnenHome'
        ? PageBackground.CONTRACT_CREATION_BLOCKED
        : undefined,
    );
  } else if (!offer) {
    setCurrentBackground();
    content = <FullContentMessage text={t('dataMissingMessage')} />;
  } else {
    setCurrentBackground();
  }

  return (
    <div className={styles.offerCalculationPage} data-testid={dataTestId}>
      <OfferCalculationForm />

      <OrderFlowPage
        sideCard={!offer || contractCreationBlocked ? null : sideCard}
        title={title}
        className={styles.layoutContainer}
      >
        {content}
      </OrderFlowPage>
    </div>
  );
};
